import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { RouterProvider } from "react-router-dom";
import { ScreenLoaderWhite } from "../components/CustomComponents/ScreenLoaderWhite";
import { isOnboardingDemoRoute } from "../pages/Onboarding/isDemoRoute";
import { useOnboardingGate } from "../pages/Onboarding/onboardingGate";
import { mobileRouter } from "./mobile";
import { useAppSelector } from "../hooks/redux";
import { publicRouter } from "./public";
import { useRouterHook } from "../hooks/useRouterHook";
import {
  amplifyProvider,
  authController,
  circadiaEngineProvider,
  clinicController,
  localStorageProvider,
  notificationController,
  patientController,
  timeProvider,
  watchlistController,
  summaryController,
  homeHealthController,
  pushNotificationProvider,
  smsController,
  userController,
} from "../AppController";
import { appNotification } from "../util/appNotification";
import useSWRImmutable from "swr/immutable";
import { restrictedRouter } from "./restricted";
import { isPIN } from "../util/pin";
import VitalsRedirectHandler from "../components/VitalsRedirectHandler";
import { useVitalsSubdomainRedirect } from "../hooks/useVitalsSubdomainRedirect";
import { useAndroidAuth } from "../hooks/useAndroidAuth";
import { useVitalsAIMode } from "../hooks/useVitalsAIMode";
import { scheduleTokenAutoRefresh } from "../hexagon/core/impl/authServiceImpl";
import { isOnboardingExcludedAccount } from "../pages/Onboarding/onboardingEligibility";
import { useUse2026UI } from "../hooks/useUse2026UI";
import { isCompassusSubdomain } from "../util/subdomain";

const OnboardingDemo = lazy(() => import("../pages/Onboarding"));

export const AppRouter = () => {
  // Re-evaluate the onboarding demo gate when the URL changes without a full
  // page reload — e.g. appending #/onboarding to an already-open app, or
  // navigating over a forwarded port/tunnel. Without this, a hash-only change
  // does not re-render this component and the gate never fires.
  const [, setUrlTick] = useState(0);
  useEffect(() => {
    const onUrlChange = () => setUrlTick((n) => n + 1);
    window.addEventListener("hashchange", onUrlChange);
    window.addEventListener("popstate", onUrlChange);
    return () => {
      window.removeEventListener("hashchange", onUrlChange);
      window.removeEventListener("popstate", onUrlChange);
    };
  }, []);

  // Handle vitals subdomain redirect - backup check
  useVitalsSubdomainRedirect();

  // Handle Android WebView authentication
  useAndroidAuth();

  // Check if user is in Vitals AI mode (subdomain or beta feature)
  const isVitalsAIMode = useVitalsAIMode();

  const { getAuthState, populateAppData } = useRouterHook({
    amplifyProvider: amplifyProvider,
    authController: authController,
    clinicController: clinicController,
    localStorageProvider: localStorageProvider,
    notificationController: notificationController,
    watchlistController: watchlistController,
    timeProvider: timeProvider,
    appNotification: appNotification,
    patientController: patientController,
    circadiaEngineProvider,
    summaryController,
    homeHealthController,
    isPIN,
    smsController: smsController,
    userController: userController,
  });
  const isBiometric = localStorageProvider.get("isBiometric");
  const getAuthStateP = async () => {
    // Skip PIN for Vitals AI mode users
    if (isPIN(isVitalsAIMode)) {
      const authState = await getAuthState();

      if (isBiometric && isBiometric === "true" && authState === "biometric") {
        await populateAppData();
        // Schedule auto-refresh for SSO users
        if (localStorageProvider.get("sso") === "true") {
          scheduleTokenAutoRefresh();
        }
      }
    } else {
      const authState = await getAuthState();
      if (authState !== "notAuthorized") {
        await Promise.all([
          populateAppData(),
          pushNotificationProvider.registerDeviceForPushNotifications(),
        ]);
        // Schedule auto-refresh for SSO users
        if (localStorageProvider.get("sso") === "true") {
          scheduleTokenAutoRefresh();
        }
      }
    }
  };
  useSWRImmutable("getAuthState", getAuthStateP);

  const { isAuthorized, isPinAuthorized, hasNoAccess, user } = useAppSelector(
    (state) => {
      return {
        isAuthorized: state.auth.isAuthorized,
        isPinAuthorized: state.auth.isPinAuthorized,
        hasNoAccess: state.auth.hasNoAccess,
        user: state.auth.user,
      };
    }
  );

  // External home-health accounts use the Compassus experience rather than the
  // standard CIP product, so they do not enter CIP onboarding. The exclusion is
  // account/session based and deliberately independent of the selected clinic.
  const onboardingExcluded = isOnboardingExcludedAccount(user);

  // Onboarding ships as part of the 2026 ("2.0") UI, so only users who are
  // actually seeing that UI may be routed through it. This mirrors the
  // user∩clinic `cip2.0-ui` intersection (and the Compassus exclusion) used to
  // render the rest of the 2.0 experience.
  const use2026UI = useUse2026UI();

  // Check onboarding only after the user is fully cleared for the authenticated
  // app AND is on the 2026 UI. Hook is called unconditionally to satisfy the
  // rules of hooks; the `enabled` flag scopes when it actually checks.
  const onboardingGateEnabled =
    !!isAuthorized &&
    (isPIN(isVitalsAIMode) ? !!isPinAuthorized : true) &&
    !hasNoAccess &&
    !onboardingExcluded &&
    use2026UI;
  const onboardingGate = useOnboardingGate(onboardingGateEnabled);

  // Built exactly once: swapping the router instance remounts the entire
  // mounted route tree (pages lose state and restart their fetches, visible
  // as a load → blank → reload flash when boot-time state like the beta
  // features list or connectivity settles). Beta gates (Neo, Telehealth)
  // live in ``BetaRouteGuard`` inside the route tree, reading entitlements
  // from redux at render time.
  const mobileRouterInstance = useMemo(() => mobileRouter(), []);

  // Onboarding: reachable in any auth state via /#/onboarding or
  // ?onboarding=1. Rendered full-screen, outside the auth routers, so the
  // login/signup steps can render before the user is authenticated.
  // Not on the Compassus subdomain, which has no onboarding route.
  if (!isCompassusSubdomain() && isOnboardingDemoRoute()) {
    return (
      <Suspense fallback={<ScreenLoaderWhite />}>
        <OnboardingDemo />
      </Suspense>
    );
  }

  // Skip PIN requirement for Vitals AI mode users
  if (!isAuthorized || (isPIN(isVitalsAIMode) ? !isPinAuthorized : false)) {
    return <RouterProvider router={publicRouter} />;
  }

  if (hasNoAccess) {
    return <RouterProvider router={restrictedRouter} />;
  }

  // Invited/new users must finish onboarding before entering the app. While the
  // gate verifies status show the loader; if onboarding is owed, render it
  // full-screen (same page as the /onboarding route) across all platforms.
  if (onboardingGate === "checking") {
    return <ScreenLoaderWhite />;
  }
  if (onboardingGate === "onboard") {
    return (
      <Suspense fallback={<ScreenLoaderWhite />}>
        <OnboardingDemo />
      </Suspense>
    );
  }

  // circadiaEngineProvider.changeAppTopAndBottom("#f5f5f5", "#fff");
  return (
    <>
      <VitalsRedirectHandler />
      <RouterProvider router={mobileRouterInstance} />
    </>
  );
};
